import {campaignTypeConfigs} from 'common/src/modules/campaigns/campaigns.config';
import {CampaignSubTypeName, CampaignTypeName} from 'common/src/modules/campaigns/campaigns.types';
import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {CLStore} from 'common/src/redux/store';
import maxBy from 'lodash/maxBy';
import {AICampaignVersionStatus} from '../copilotCampaigns.enums';
import getAICampaignVersionStatus from '../utils/getAICampaignVersionStatus';

type CampaignInfoInCampaignContext = {
  campaignId: number;
  campaignName: string;
  campaignType: CampaignTypeName;
  campaignSubtype: CampaignSubTypeName;
  versionNumber: number;
  latestVersion: {
    id: number;
    number: number;
    isDraft: boolean;
    nodesCount: number;
  } | null;
  recentVersions: Array<{
    number: number;
    id: number;
    name: string;
    status: AICampaignVersionStatus;
  }>;
};

type CampaignContext = {
  campaignInfo: CampaignInfoInCampaignContext | null;
};

function campaignContext(store: CLStore): AgentContext<CampaignContext> {
  function getter(): CampaignContext {
    const campaignTree = store.getState().campaignPage.campaignTree;

    if (!campaignTree || !campaignTree.campaign) {
      return {campaignInfo: null};
    }

    const {campaign, campaignVersions: versions, versionNumber} = campaignTree;

    const latestVersion = maxBy(versions, 'number');

    const campaignTypeConfig = campaignTypeConfigs.find((config) => config.id === campaign.type);

    const campaignSubtypeConfig = campaignTypeConfig?.subtypes.find(
      (subtype) => subtype.id === campaign.subType,
    );

    const recentVersions = [...versions]
      .sort((a, b) => b.number - a.number)
      .slice(0, 10)
      .map((version) => {
        const status = getAICampaignVersionStatus(version);

        return {
          number: version.number,
          id: version.id,
          name: version.name || 'Unnamed',
          status,
        };
      });

    return {
      campaignInfo: {
        campaignId: campaign.id,
        campaignName: campaign.name || '',
        campaignType: campaignTypeConfig?.type,
        campaignSubtype: campaignSubtypeConfig?.type,
        versionNumber,
        latestVersion: latestVersion
          ? {
              id: latestVersion.id,
              number: latestVersion.number,
              isDraft: latestVersion.isDraft,
              nodesCount: latestVersion.nodesCount,
            }
          : null,
        recentVersions,
      },
    };
  }

  function formatter({currentContext}: {currentContext: CampaignContext}): string {
    const {campaignInfo} = currentContext;

    if (!campaignInfo) {
      return 'The user is currently not viewing a campaign.';
    }

    const {
      campaignId,
      campaignName,
      campaignType,
      campaignSubtype,
      versionNumber,
      latestVersion,
      recentVersions,
    } = campaignInfo;

    const campaignTypeConfig = campaignTypeConfigs.find((config) => config.type === campaignType);

    const campaignSubtypeConfig = campaignTypeConfig?.subtypes.find(
      (subtype) => subtype.type === campaignSubtype,
    );

    const versionHistory = recentVersions
      ? recentVersions
          .map(
            (version) =>
              `Version number: ${version.number} Version ID: ${version.id} (Status: ${version.status})${
                version.number === versionNumber ? ' <= This is the current version' : ''
              }`,
          )
          .join('\n')
      : null;

    const versionHistoryDetails = versionHistory
      ? `- Last 10 versions (latest version is first, current version is highlighted):
${versionHistory}
- Number of cards in the latest version: ${latestVersion?.nodesCount}`
      : '';

    return `- Campaign ID: ${campaignId}
- Campaign name: ${campaignName}
- Campaign type: ${campaignTypeConfig ? `${campaignTypeConfig?.label} (${campaignTypeConfig?.isSocial ? 'social' : 'non-social'})` : 'unknown'}
- Campaign subtype: ${campaignSubtypeConfig?.label || 'none'}
${versionHistoryDetails}
`;
  }

  return {
    title: 'Current campaign',
    getter,
    formatter,
  };
}

export default campaignContext;
